import type { FC, FormEvent, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { useI18n } from '@/i18n'
import { Button } from '@/components/ui/button'
import { controlClass } from '@/components/ui/field'

interface QuickRequestProps {
    locations: Array<string>
    today: string
}

export const QuickRequest: FC<QuickRequestProps> = ({ locations, today }): ReactNode => {
    const router = useRouter()
    const { t } = useI18n()
    const [destination, setDestination] = useState('')
    const [arrival, setArrival] = useState('')
    const [departure, setDeparture] = useState('')
    const [guests, setGuests] = useState('2')

    const submit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const query: Record<string, string> = {}

        if (destination) query.destination = destination
        if (arrival) query.arrival = arrival
        if (departure && (!arrival || departure > arrival)) query.departure = departure
        if (guests) query.adults = guests

        router.push({ pathname: '/booking', query })
    }

    const labelClass = 'text-[11px] font-bold tracking-[0.18em] text-muted-foreground uppercase'

    return (
        <form
            onSubmit={submit}
            aria-label={t.home.searchTitle}
            className='grid gap-4 rounded-3xl border border-white/40 bg-white/95 p-5 shadow-2xl backdrop-blur sm:grid-cols-2 lg:grid-cols-[1.2fr_1fr_1fr_0.7fr_auto] lg:items-end lg:p-6'
        >
            <div className='flex flex-col gap-1.5'>
                <label htmlFor='quick-destination' className={labelClass}>{t.home.searchDestination}</label>
                <select id='quick-destination' value={destination} onChange={event => setDestination(event.target.value)} className={controlClass(false, 'h-11 cursor-pointer')}>
                    <option value=''>{t.home.anyDestination}</option>
                    {locations.map(location => <option key={location} value={location}>{location}</option>)}
                </select>
            </div>

            <div className='flex flex-col gap-1.5'>
                <label htmlFor='quick-arrival' className={labelClass}>{t.home.searchArrival}</label>
                <input id='quick-arrival' type='date' min={today} value={arrival} onChange={event => setArrival(event.target.value)} className={controlClass(false, 'h-11')} />
            </div>

            <div className='flex flex-col gap-1.5'>
                <label htmlFor='quick-departure' className={labelClass}>{t.home.searchDeparture}</label>
                <input id='quick-departure' type='date' min={arrival || today} value={departure} onChange={event => setDeparture(event.target.value)} className={controlClass(false, 'h-11')} />
            </div>

            <div className='flex flex-col gap-1.5'>
                <label htmlFor='quick-guests' className={labelClass}>{t.home.searchGuests}</label>
                <input id='quick-guests' type='number' inputMode='numeric' min={1} max={30} value={guests} onChange={event => setGuests(event.target.value)} className={controlClass(false, 'h-11')} />
            </div>

            <Button type='submit' variant='primary' size='lg' className='w-full sm:col-span-2 lg:col-span-1 lg:w-auto'>
                {t.home.searchButton}
                <ArrowRight className='size-4 rtl:rotate-180' aria-hidden='true' />
            </Button>
        </form>
    )
}
